import { useMemo } from 'react'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import LineKeySkeletonIcon from 'LuxKit/Icons/line/LineKeySkeletonIcon'
import { useIntl } from 'react-intl'

interface Props {
  offer: App.CarHireOffer
  onViewInclusions: () => void
  showViewDetails?: boolean
}

function CarHireOfferTileInclusions(props: Props) {
  const { offer, onViewInclusions, showViewDetails = true } = props
  const intl = useIntl()

  const unlimitedMileageLabel = intl.formatMessage({
    id: 'J71byfgXFEBzaOBf8si2',
    defaultMessage: 'Unlimited mileage',
    description: 'Inclusion label indicating unlimited mileage for car hire offer',
  })

  const limitedMileageLabel = intl.formatMessage(
    {
      id: '1tS12Nnl792Dwe2axBIp',
      defaultMessage: '{quantity}{unit}/{period} rental mileage limit',
      description: 'Inclusion label showing rental mileage limit for car hire offer, e.g. 300km/day rental mileage limit',
    },
    {
      quantity: offer.mileage.quantity,
      unit: offer.mileage.unit,
      period: offer.mileage.period,
    },
  )

  const inclusionsList = useMemo(() => {
    const inclusions = [...offer.copy.inclusions]

    inclusions.sort((inclusion) => {
      if (inclusion.toLowerCase().includes('additional driver')) return -2
      if (inclusion.toLowerCase().includes('fee')) return -1
      return 0
    })

    inclusions.unshift(offer.mileage.unlimited ? unlimitedMileageLabel : limitedMileageLabel)

    return inclusions.slice(0, 3)
  }, [offer, unlimitedMileageLabel, limitedMileageLabel])

  const viewAllDetailsLabel = intl.formatMessage({
    id: 'aNHAqPOISpUt0tknry4E',
    defaultMessage: 'View all details',
    description: 'Link text to view all car hire offer inclusions and details',
  })

  return <VerticalSpacer gap={4}>
    {inclusionsList.length > 0 && <>
      {inclusionsList.map((inclusion, index) =>
        <Group
          direction="horizontal"
          gap={4}
          verticalAlign="center"
          key={inclusion}
        >
          {index === 0 && <LineKeySkeletonIcon colour="highlight-primary-dark" />}
          {index !== 0 && <LineCheckIcon />}
          <BodyText variant="medium" colour={index === 0 ? 'highlight-primary-dark' : 'light-foreground-alt'}>{inclusion}</BodyText>
        </Group>,
      )}
    </>}
    {showViewDetails && <TextLink size="medium" onClick={onViewInclusions}>
      {viewAllDetailsLabel}
    </TextLink>}
  </VerticalSpacer>
}

export default CarHireOfferTileInclusions
